import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MessageDialogService } from '../../services/message-dialog';
import { SelectedProduct } from '../../services/products';
import { firstValueFrom } from 'rxjs';
import { CustomerService, Customer } from '../../services/customer';
import { DeliveryBoy, FilteredLoad, LoadDetail, LoadManagementService } from '../../services/load-management.service';
import { MatIconModule } from '@angular/material/icon';
import { LoadViewerComponent } from '../../components/load-viewer-component/load-viewer-component';
import { LoadTableComponent } from '../../components/load-table-component/load-table-component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';

@Component({
  selector: 'app-load-report',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatButtonModule,
    MatCardModule,
    MatIconModule,
    LoadViewerComponent,
    LoadTableComponent,
    MatSelectModule 
  ],
  templateUrl: './delivery-report-component.html',
  styleUrls: ['./delivery-report-component.scss']
})
export class DeliveryReportComponent implements OnInit {
  startDate: string = '';
  endDate: string = '';
  page: number = 1;
  pageSize: number = 10;
  totalPages: number = 1;
  selectedUserId: number | null = null
  loads: FilteredLoad[] = [];
  showCart = false;
  deliveryBoys: DeliveryBoy[] = [];
  selectedLoad: LoadDetail | null = null;
  selectedProducts: SelectedProduct[] = [];
  selectedCustomer: Customer | null = null;

  constructor(
    private loadService: LoadManagementService,
    private messageDialogService: MessageDialogService,
    private customerService: CustomerService
  ) {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);

    this.startDate = today.toISOString().split('T')[0];
    this.endDate = tomorrow.toISOString().split('T')[0];
  }

  ngOnInit(): void {
    this.fetchDeliveryBoys();
  }

  fetchLoads(): void {
    if (!this.startDate || !this.endDate) {
      this.messageDialogService.warning('Please select start and end dates.');
      return;
    }

    const payload = {
      startDate: this.startDate,
      endDate: this.endDate,
      page: this.page,
      page_size: this.pageSize
    };

    this.loadService.filterLoads(payload).subscribe((res) => {
      if (res.status === 'success' && res.loads) {
        this.loads = res.loads;
        this.totalPages = res.pages ?? 1;
      } else {
        this.loads = [];
        this.totalPages = 1;
      }
    });
  }

  viewLoadDetails(load: FilteredLoad) {
    this.loadService.getLoadById({ load_id: load.id }).subscribe(async (res) => {
      if (res.status !== 'success' || !res.load) {
        this.messageDialogService.error(res.message || 'Unable to load details');
        return;
      }

      this.selectedLoad = res.load;
      this.selectedProducts = res.load.saleOrders.map((line) => ({
        productId: line.id,
        quantity: line.numberOfItems,
        price: line.totalAmount // Note: adjust this if your cart expects unit price
      }));

      const customers = await firstValueFrom(this.customerService.get());
      const firstOrder = res.load.saleOrders[0];
      this.selectedCustomer = customers.find(c => c.id === firstOrder.customerId) ?? null;

      this.showCart = true;
    });
  }

  nextPage(): void {
    if (this.page < this.totalPages) {
      this.page++;
      this.fetchLoads();
    }
  }

  prevPage(): void {
    if (this.page > 1) {
      this.page--;
      this.fetchLoads();
    }
  }

  onReloadDetails() {
    this.showCart = false;
    this.fetchLoads();
  }

  // Method to handle assigning delivery
  assignDelivery() {
    if (!this.selectedUserId) {
      this.messageDialogService.warning('Please select a delivery person.');
      return;
    }

    const selectedLoads = this.loads.filter(load => load.selected);
    const selectedLoadIds = selectedLoads.map(load => load.id);

    if (selectedLoadIds.length === 0) {
      this.messageDialogService.warning('Please select at least one load.');
      return;
    }

    const conflictLoads = selectedLoads.filter(
      load => load.deliveryBoyId && load.deliveryBoyId !== this.selectedUserId
    );

    const proceedWithAssignment = () => {
      const payload = {
        delivery_boy_id: this.selectedUserId ?? 0,
        load_ids: selectedLoadIds
      };

      this.loadService.assignDelivery(payload).subscribe(res => {
        if (res.status === 'success') {
          this.messageDialogService.success('Delivery assigned successfully!');
          this.fetchLoads();
        } else {
          this.messageDialogService.error(res.message || 'Failed to assign delivery.');
        }
      });
    };

    const confirmCombine = () => {
      if (selectedLoadIds.length > 1) {
        this.messageDialogService.confirm(
          `You are about to combine ${selectedLoadIds.length} loads. Are you sure?`
        ).subscribe(confirmed => {
          if (confirmed) proceedWithAssignment();
        });
      } else {
        proceedWithAssignment();
      }
    };

    if (conflictLoads.length > 0) {
      this.messageDialogService.confirm(
        `One or more selected loads are already assigned to a different delivery person. Do you want to override the assignment?`
      ).subscribe(confirmed => {
        if (confirmed) confirmCombine();
      });
    } else {
      confirmCombine();
    }
  }


  fetchDeliveryBoys(): void {
    this.loadService.getDeliveryBoys().subscribe(res => {
      if (res.status === 'success' && res.users) {
        this.deliveryBoys = res.users;
      } else {
        this.messageDialogService.warning(res.message || 'Could not load delivery boys');
        this.deliveryBoys = [];
      }
    });
  }
}
